<script>
  import { onMount } from "svelte";
  import Header from "../components/Header.svelte";
  let blogs = [];
  let loading = true;

  onMount(async () => {
    const res = await fetch("/blogs.json");
    blogs = await res.json();
    loading = false;
  });

  function openBlogUrl(url) {
    window.open(url, "_blank", "noopener");
  }
</script>

<section id="blog" class="pt-10 md:pt-20">
  <div class=" px-20">
    <Header title="Blog" subtitle="Latest articles & updates" />
    {#if loading}
      <div class="text-xl animate-pulse text-center">Loading...</div>
    {:else}
      <div
        class="flex flex-row pb-4 no-scrollbar gap-3 place-content-center content-center"
      >
        {#each blogs.slice(0, 3) as blog}
          <div
            class="flex flex-col flex-1 bg-white/30 py-10 dark:bg-gray-800/20 backdrop-blur-xl border border-white/30 dark:border-gray-700/30 rounded-3xl p-6 md:p-8 flex-shrink-0 shadow-lg hover:scale-[1.02] hover:shadow-[0_20px_40px_-15px_rgba(59,130,246,0.15)] dark:hover:shadow-[0_20px_40px_rgba(59,130,246,0.15)] hover:border-blue-100/50 dark:hover:border-blue-500/30 transition-all duration-500 group cursor-pointer"
            on:click={() => openBlogUrl(blog.blogUrl)}
            tabindex="0"
            on:keydown={(e) => {
              if (e.key === "Enter" || e.key === " ") openBlogUrl(blog.blogUrl);
            }}
            role="button"
            aria-label={blog.title}
          >
            <img
              src={blog.blogImageUrl}
              alt={blog.title}
              class="w-full h-48 p-2 object-cover rounded-lg mb-4 border-2 border-white/50 dark:border-gray-700/50 shadow"
            />
            <div class="flex items-center gap-3 mb-3">
              <img
                src={blog.publisherImageUrl}
                alt="Publisher"
                class="w-8 h-8 rounded-full border-2 border-white/50 dark:border-gray-700/50"
              />
              <span class="text-xs text-gray-500 dark:text-gray-400"
                >{blog.date}</span
              >
            </div>
            <div
              class="text-lg font-semibold text-blue-600 dark:text-blue-400 group-hover:underline mb-2 line-clamp-2"
            >
              {blog.title}
            </div>
            <div class="text-gray-700 dark:text-gray-200 mb-2 line-clamp-3">
              {blog.description}
            </div>
            <div
              class="mt-4 text-blue-500 dark:text-blue-400 font-medium group-hover:underline flex items-center gap-1"
            >
              Read Blog <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
                ><path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                /></svg
              >
            </div>
          </div>
        {/each}

        {#if blogs.length > 3}
          <!-- View More Card -->
          <a
            href="/blog"
            class="bg-gradient-to-br from-blue-500/20 to-purple-600/20 dark:from-blue-500/30 dark:to-purple-600/30 backdrop-blur-xl border-2 border-dashed border-blue-300/50 dark:border-blue-500/50 rounded-3xl p-6 md:p-8 min-w-[200px] max-w-[200px] flex-shrink-0 shadow-lg hover:scale-[1.02] hover:shadow-[0_20px_40px_-15px_rgba(59,130,246,0.25)] dark:hover:shadow-[0_20px_40px_rgba(59,130,246,0.25)] hover:border-blue-400/70 dark:hover:border-blue-400/70 transition-all duration-500 group cursor-pointer flex flex-col items-center justify-center text-center relative overflow-hidden"
            tabindex="0"
            role="button"
            aria-label="View all blogs"
          >
            <!-- Background pattern -->
            <div class="absolute inset-0 opacity-10 dark:opacity-20">
              <div
                class="absolute top-4 left-4 w-8 h-8 rounded-full bg-blue-500 animate-pulse"
              ></div>
              <div
                class="absolute top-12 right-8 w-4 h-4 rounded-full bg-purple-500 animate-pulse"
                style="animation-delay: 1s"
              ></div>
              <div
                class="absolute bottom-8 left-12 w-6 h-6 rounded-full bg-cyan-500 animate-pulse"
                style="animation-delay: 2s"
              ></div>
              <div
                class="absolute bottom-4 right-4 w-3 h-3 rounded-full bg-pink-500 animate-pulse"
                style="animation-delay: 0.5s"
              ></div>
            </div>

            <!-- Content -->
            <div class="relative z-10">
              <div
                class="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300"
              >
                <svg
                  class="w-10 h-10 text-white"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>

              <h3
                class="text-xl font-bold text-gray-800 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
              >
                View All Blogs
              </h3>

              <p class="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                Discover {blogs.length - 3}+ more articles
              </p>

              <div
                class="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium group-hover:gap-3 transition-all"
              >
                <span>Explore More</span>
                <svg
                  class="w-5 h-5 group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </div>
            </div>
          </a>
        {/if}
      </div>
    {/if}
  </div>
</section>

<style>
  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-clamp: 3;
  }
</style>
